// ---------------------------------------------------------------------------
// StatusBanner — Full-width colored banner showing template status
// ---------------------------------------------------------------------------

import { Badge } from '@/components/ui/badge';
import { statusLabel } from '@/lib/utils';
import type { TemplateStatus } from '@/types/template';

const STATUS_STYLES: Record<TemplateStatus, string> = {
  draft: 'bg-silver-50 border-silver-200',
  pending_internal_review: 'bg-purple-50 border-purple-200',
  tcm_admin_approved: 'bg-pacific-50 border-pacific-200',
  pending_client_review: 'bg-purple-50 border-purple-200',
  live: 'bg-green-50 border-green-200',
  revision_requested: 'bg-honey-50 border-honey-200',
};

const STATUS_BADGE: Record<TemplateStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  draft: 'secondary',
  pending_internal_review: 'default',
  tcm_admin_approved: 'default',
  pending_client_review: 'default',
  live: 'success',
  revision_requested: 'warning',
};

interface StatusBannerProps {
  status: TemplateStatus;
  title: string;
  createdBy: string;
  children?: React.ReactNode;
}

export function StatusBanner({ status, title, createdBy, children }: StatusBannerProps) {
  return (
    <div
      className={`flex items-center justify-between rounded-ds-008 border px-5 py-4 ${STATUS_STYLES[status]}`}
    >
      <div className="flex items-center gap-3">
        <Badge variant={STATUS_BADGE[status]}>
          {statusLabel(status)}
        </Badge>
        <div>
          <h2 className="text-lg font-semibold text-carbon-900">{title}</h2>
          <p className="text-xs text-carbon-500">Created by {createdBy}</p>
        </div>
      </div>
      {children && (
        <div className="flex items-center gap-2 shrink-0">
          {children}
        </div>
      )}
    </div>
  );
}
